import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Eye, MousePointerClick, Users, TrendingUp } from 'lucide-react';

interface WorkshopStat {
  workshop_id: string;
  title: string;
  detail_views: number;
  booking_clicks: number;
  nearby_impressions: number;
  card_clicks: number;
}

interface LeaderStat {
  profile_views: number;
  website_clicks: number;
  total_detail_views: number;
  total_booking_clicks: number;
}

interface Props {
  leaderId: string;
}

export const WorkshopStats: React.FC<Props> = ({ leaderId }) => {
  const [days, setDays] = useState('30');
  const [workshopStats, setWorkshopStats] = useState<WorkshopStat[]>([]);
  const [leaderStats, setLeaderStats] = useState<LeaderStat | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, [leaderId, days]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const [workshopRes, leaderRes] = await Promise.all([
        supabase.rpc('workshop_stats', { p_leader_id: leaderId, p_days: parseInt(days) }),
        supabase.rpc('workshop_leader_stats', { p_leader_id: leaderId, p_days: parseInt(days) }),
      ]);

      if (workshopRes.data) setWorkshopStats(workshopRes.data);
      if (leaderRes.data && leaderRes.data.length > 0) setLeaderStats(leaderRes.data[0]);
    } catch (err) {
      console.error('Error loading workshop stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const totalViews = leaderStats?.total_detail_views || 0;
  const totalClicks = leaderStats?.total_booking_clicks || 0;
  const ctr = totalViews > 0 ? ((totalClicks / totalViews) * 100).toFixed(1) : '0.0';

  const dayLabel = days === '7' ? '7 days' : days === '30' ? '30 days' : days === '90' ? '90 days' : 'all time';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">Performance</h2>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-[130px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">All time</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Eye className="w-4 h-4" />
              <span className="text-xs font-medium uppercase tracking-wide">Views</span>
            </div>
            <span className="text-2xl font-bold text-foreground">
              {loading ? '—' : totalViews}
            </span>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <MousePointerClick className="w-4 h-4" />
              <span className="text-xs font-medium uppercase tracking-wide">Clicks</span>
            </div>
            <span className="text-2xl font-bold text-foreground">
              {loading ? '—' : totalClicks}
            </span>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <TrendingUp className="w-4 h-4" />
              <span className="text-xs font-medium uppercase tracking-wide">CTR</span>
            </div>
            <span className="text-2xl font-bold text-foreground">
              {loading ? '—' : `${ctr}%`}
            </span>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Users className="w-4 h-4" />
              <span className="text-xs font-medium uppercase tracking-wide">Profile</span>
            </div>
            <span className="text-2xl font-bold text-foreground">
              {loading ? '—' : (leaderStats?.profile_views || 0)}
            </span>
          </CardContent>
        </Card>
      </div>

      {/* Per-workshop breakdown */}
      {!loading && workshopStats.length > 0 && (
        <Card>
          <CardContent className="pt-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">By Workshop ({dayLabel})</h3>
            <div className="space-y-0 divide-y divide-border">
              {workshopStats.map(ws => (
                <div key={ws.workshop_id} className="flex items-center justify-between py-2.5">
                  <span className="text-sm text-foreground truncate flex-1 mr-4">{ws.title}</span>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground flex-shrink-0">
                    <span title="Detail views">
                      <Eye className="w-3.5 h-3.5 inline mr-1" />
                      {ws.detail_views}
                    </span>
                    <span title="Booking clicks">
                      <MousePointerClick className="w-3.5 h-3.5 inline mr-1" />
                      {ws.booking_clicks}
                    </span>
                    <span
                      className="font-medium text-foreground w-14 text-right"
                      title="Click-through rate"
                    >
                      {ws.detail_views > 0
                        ? `${((ws.booking_clicks / ws.detail_views) * 100).toFixed(0)}%`
                        : '—'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && workshopStats.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">
          No analytics data yet. Stats will appear once visitors view your workshops.
        </p>
      )}
    </div>
  );
};
